import React from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import RepeatIcon from '@atlaskit/icon-lab/core/repeat';
import ClockIcon from '@atlaskit/icon/core/clock';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import ScheduledChatHeaderImage from '../../../assets/scheduled-chat-header.svg';
import { BasePreviewCard } from '../../../common/ui/base-preview-card';

import { messages } from './messages';

const styles = cssMap({
	imageSection: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	detailsSection: {
		marginTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingBottom: token('space.200'),
	},
	footerActions: {
		width: '100%',
	},
});

export type ScheduledChatPreviewCardProps = {
	title: string;
	repeatLabel: string;
	nextRunLabel: string;
	isSelected?: boolean;
	isEnabled?: boolean;
	onDeleteClick?: () => void;
	onEditClick?: () => void;
};

type DetailRowProps = {
	label: string;
	value: string;
	icon: React.ReactNode;
};

const DetailRow = ({ label, value, icon }: DetailRowProps): React.JSX.Element => (
	<Inline alignBlock="center" spread="space-between">
		<Inline alignBlock="center" space="space.100">
			{icon}
			<Text color="color.text.subtle" size="medium">
				{label}
			</Text>
		</Inline>
		<Text color="color.text" size="medium">
			{value}
		</Text>
	</Inline>
);

export const ScheduledChatPreviewCard = ({
	title,
	repeatLabel,
	nextRunLabel,
	isSelected = false,
	isEnabled = true,
	onDeleteClick,
	onEditClick,
}: ScheduledChatPreviewCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<BasePreviewCard
			isSelected={isSelected}
			isEnabled={isEnabled}
			isCollapsible
			noPadding={true}
			previewProductLogo={<ClockIcon label="" color={token('color.icon.subtle')} />}
			previewHeaderTitle={
				<Stack space="space.0">
					<Text size="medium" weight="bold">
						{title}
					</Text>
					<Text size="small" color="color.text.subtle">
						{formatMessage(messages.nextRunAt, { nextRunLabel })}
					</Text>
				</Stack>
			}
			previewContent={
				<Stack>
					<Stack xcss={styles.imageSection}>
						<img src={ScheduledChatHeaderImage} alt="" />
					</Stack>
					<Stack space="space.150" xcss={styles.detailsSection}>
						<DetailRow
							label={formatMessage(messages.repeat)}
							value={repeatLabel}
							icon={<RepeatIcon label="" color={token('color.icon.subtle')} />}
						/>
						<DetailRow
							label={formatMessage(messages.nextRun)}
							value={nextRunLabel}
							icon={<ClockIcon label="" color={token('color.icon.subtle')} />}
						/>
					</Stack>
				</Stack>
			}
			previewFooterActions={
				<Inline xcss={styles.footerActions} alignInline="end" alignBlock="center" space="space.100">
					<Button appearance="default" onClick={onDeleteClick}>
						{formatMessage(messages.delete)}
					</Button>
					<Button appearance="primary" onClick={onEditClick}>
						{formatMessage(messages.edit)}
					</Button>
				</Inline>
			}
		/>
	);
};
